import {Link} from '@tanstack/react-router';
import {useEffect} from 'react';

import {Heading} from '../../components/Heading';
import {Text} from '../../components/Text';
import {extractErrorMessage} from '../../functions/extractErrorMessage';
import {handleError} from '../../functions/handleError';
import {SUPPORT_EMAIL} from '../../utils/constants';
import {AppError} from '../../utils/errors';

import {ErrorScreenBody, ErrorScreenContainer, ErrorScreenIcon} from './common';
import {ERROR_KIND_CONFIG} from './errorKindConfig';
import {isLocationSegmentationRequired} from './locationSegmentationRequired';

import type {AppErrorKind} from '../../utils/errors';

type Props = {
  error: unknown;
  handleRetry: () => void;
  kind?: AppErrorKind;
  title?: string;
};
export function MainErrorScreen({
  error,
  handleRetry,
  kind,
  title,
}: Props): React.JSX.Element {
  const resolvedKind =
    kind ?? (error instanceof AppError ? error.kind : 'critical');
  const resolvedTitle =
    title ?? (error instanceof AppError ? error.title : undefined);

  const {icon, iconClassName, defaultTitle} = ERROR_KIND_CONFIG[resolvedKind];

  useEffect(() => {
    handleError(error);
  }, [error]);

  const message = extractErrorMessage(error);

  return (
    <div className="flex min-h-screen w-full items-center justify-center">
      <ErrorScreenContainer>
        <ErrorScreenIcon icon={icon} className={iconClassName} />

        <ErrorScreenBody className="text-slate12">
          <Heading as="title" tag="h1">
            {resolvedTitle ?? defaultTitle}
          </Heading>

          {message && <Text>{message}</Text>}

          {isLocationSegmentationRequired(error) ? (
            <Text>
              Reloading won&apos;t fix this. Please{' '}
              <Text as="link" tag="span">
                <Link to="/locations">complete location segmentation</Link>
              </Text>{' '}
              to continue.
            </Text>
          ) : (
            <Text>
              Please{' '}
              <Text as="link" tag="span">
                <button onClick={handleRetry}>reload the page</button>
              </Text>
              . If the problem persists, send an email to{' '}
              <Text as="link" tag="span">
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </Text>
              .
            </Text>
          )}
        </ErrorScreenBody>
      </ErrorScreenContainer>
    </div>
  );
}
